import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSync,
  FaPlay,
  FaStop,
  FaPause,
  FaServer
} from 'react-icons/fa';
import { FaDocker } from 'react-icons/fa6';
import { getDockerContainers } from '../api/client';

const DockerMonitoring = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchContainers = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const result = await getDockerContainers();
      setData(result);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Error loading Docker containers:', err);
      if (err.response?.status === 403) {
        setError('You do not have access to Docker monitoring.');
      } else if (err.response?.status === 503) {
        setError('Unable to connect to Docker daemon.');
      } else {
        setError('Failed to load Docker containers.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContainers();
  }, [fetchContainers]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchContainers(false), 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchContainers]);

  const getStateColor = (state) => {
    switch (state) {
      case 'running': return 'text-green-600 bg-green-100';
      case 'exited': case 'dead': return 'text-red-600 bg-red-100';
      case 'paused': return 'text-yellow-600 bg-yellow-100';
      case 'restarting': return 'text-blue-600 bg-blue-100';
      case 'created': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStateIcon = (state) => {
    switch (state) {
      case 'running': return <FaPlay className="text-xs" />;
      case 'exited': case 'dead': return <FaStop className="text-xs" />;
      case 'paused': return <FaPause className="text-xs" />;
      default: return <FaServer className="text-xs" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatPorts = (ports) => {
    if (!ports || Object.keys(ports).length === 0) return '-';
    return Object.entries(ports).map(([containerPort, hostBindings]) => {
      if (hostBindings && hostBindings.length > 0) {
        return hostBindings.map(b => `${b.HostPort}->${containerPort}`).join(', ');
      }
      return containerPort;
    }).join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary-500" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  const { containers = [], summary = {}, services = [] } = data || {};

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
              <FaDocker className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-800">Docker Monitoring</h1>
              <p className="text-neutral-600">Container status and health overview</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 rounded border-neutral-300 text-blue-500 focus:ring-blue-500"
              />
              Auto-refresh (10s)
            </label>
            <button
              onClick={() => fetchContainers(false)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              <FaSync className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
        {lastRefreshed && (
          <p className="text-xs text-neutral-400 mt-2">
            Last refreshed: {lastRefreshed.toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Error banner */}
      {error && data && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FaExclamationTriangle className="text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaServer className="text-blue-600" />
            </div>
            <span className="text-sm font-medium text-neutral-600">Total</span>
          </div>
          <p className="text-3xl font-bold text-neutral-800">{summary.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FaCheckCircle className="text-green-600" />
            </div>
            <span className="text-sm font-medium text-neutral-600">Running</span>
          </div>
          <p className="text-3xl font-bold text-green-600">{summary.running || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <FaStop className="text-red-600" />
            </div>
            <span className="text-sm font-medium text-neutral-600">Stopped</span>
          </div>
          <p className="text-3xl font-bold text-red-600">{summary.stopped || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FaPause className="text-yellow-600" />
            </div>
            <span className="text-sm font-medium text-neutral-600">Paused</span>
          </div>
          <p className="text-3xl font-bold text-yellow-600">{summary.paused || 0}</p>
        </div>
      </div>

      {/* Services / Instance Counts */}
      <div className="bg-white rounded-xl shadow-md border border-neutral-200 overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaServer />
            Services ({services.length})
          </h2>
        </div>
        <div>
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[25%]">Service</th>
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[15%]">Project</th>
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[30%]">Image</th>
                <th className="text-center px-2 py-3 font-semibold text-neutral-700 w-[10%]">Instances</th>
                <th className="text-center px-2 py-3 font-semibold text-neutral-700 w-[10%]">Running</th>
                <th className="text-center px-2 py-3 font-semibold text-neutral-700 w-[10%]">Stopped</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc) => (
                <tr key={`${svc.project}-${svc.service}`} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-3 py-3 font-medium text-neutral-800 truncate" title={svc.service}>{svc.service}</td>
                  <td className="px-3 py-3 text-neutral-500 text-xs truncate" title={svc.project || '-'}>{svc.project || '-'}</td>
                  <td className="px-3 py-3 text-neutral-600 font-mono text-xs truncate" title={svc.image}>{svc.image}</td>
                  <td className="px-2 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-xs">
                      {svc.total}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-center">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs ${svc.running > 0 ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-400'}`}>
                      {svc.running}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-center">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs ${svc.stopped > 0 ? 'bg-red-100 text-red-700' : 'bg-neutral-100 text-neutral-400'}`}>
                      {svc.stopped}
                    </span>
                  </td>
                </tr>
              ))}
              {services.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-8 text-center text-neutral-500">
                    No services found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Container Table */}
      <div className="bg-white rounded-xl shadow-md border border-neutral-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaDocker />
            Containers ({containers.length})
          </h2>
        </div>
        <div>
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[22%]">Name</th>
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[22%]">Image</th>
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[10%]">State</th>
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[14%]">Status</th>
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[16%]">Ports</th>
                <th className="text-left px-3 py-3 font-semibold text-neutral-700 w-[16%]">Created</th>
              </tr>
            </thead>
            <tbody>
              {containers.map((container) => (
                <tr key={container.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-3 py-3">
                    <div className="font-medium text-neutral-800 truncate" title={container.name}>{container.name}</div>
                    <div className="text-xs text-neutral-400">{container.id}</div>
                  </td>
                  <td className="px-3 py-3 text-neutral-600 font-mono text-xs truncate" title={container.image}>{container.image}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStateColor(container.state)}`}>
                      {getStateIcon(container.state)}
                      {container.state}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-neutral-600 text-xs truncate" title={container.status}>{container.status}</td>
                  <td className="px-3 py-3 text-neutral-600 font-mono text-xs truncate" title={formatPorts(container.ports)}>{formatPorts(container.ports)}</td>
                  <td className="px-3 py-3 text-neutral-500 text-xs">{formatDate(container.created)}</td>
                </tr>
              ))}
              {containers.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-8 text-center text-neutral-500">
                    No containers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DockerMonitoring;
